import type { HealthDto, MeDto, ViewAs } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { api } from '../api.ts';
import { currentEnvironment, switchEnvironment, type Environment } from '../environment.ts';
import { isAdmin, isStaff, switchView, useMe, type View } from '../session.tsx';
import { ProjectMenu } from '../screens/project/common.tsx';
import { framed, isPublicPage } from '../publicPages.ts';
import { DevNotice } from './DevNotice.tsx';
import { KeyNotice } from './KeyNotice.tsx';
import { Lockup } from './Brand.tsx';
import { PortalFoot } from './PortalFoot.tsx';

/** Where "Projects" is current: the register itself, and every project and lead under it. */
const inProjects = (pathname: string) => pathname === '/' || pathname.startsWith('/p/') || pathname.startsWith('/leads/');

/** The frame: a thin header with the portal lockup and a few words of navigation (style guide v2 §11). */
export function Layout({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  // Sign-in carries its own, larger lockup (style guide v2 §12), so no header there, nor on the Allow page that sits as it does.
  const { pathname } = useLocation();
  const bare = !framed(pathname);
  // Signed in, every page ends on the portal's foot (design study 30), which keeps the room for the Feedback button
  // the page kept below itself; a column, so it sits at the window's foot on a short page.
  const foot = !bare && Boolean(me);
  const env = currentEnvironment();
  const nav = (to: string, label: string, current = pathname === to || pathname.startsWith(`${to}/`)) => (
    <Link
      to={to}
      aria-current={current ? 'page' : undefined}
      className={`border-b-2 py-2 text-[0.8125rem] transition-colors duration-[120ms] ${current ? 'border-ink text-ink' : 'border-transparent text-ink-2 hover:text-ink'}`}
    >
      {label}
    </Link>
  );
  return (
    <div className={`flex min-h-dvh flex-col ${foot ? '[&>main]:pb-14' : ''}`}>
      {env || me?.canViewAs ? <TopStrip env={env} me={me ?? null} /> : null}
      {/* Under development: on every page of the portal, the sign-in page too, for everyone; the public pages alone are without it. */}
      {isPublicPage(pathname) ? null : <DevNotice />}
      {!bare && isAdmin(me) ? <KeyNotice /> : null}
      {bare ? null : (
        <header className="shell flex items-center justify-between gap-4 pt-4">
          <Link to="/" aria-label="Never Stop Building portal, home" className="py-2">
            <Lockup />
          </Link>
          {me ? (
            <nav className="flex items-center gap-4 sm:gap-7">
              {isStaff(me) ? nav('/', 'Projects', inProjects(pathname)) : null}
              {isStaff(me) ? nav('/settings', 'Settings') : null}
              {nav('/account', 'Account')}
            </nav>
          ) : null}
        </header>
      )}
      {/* On a project's pages, NSB's sections of it, under the header. Its home lays them under its title, clear of the banner;
          the client view is the client's page as they see it, with its own way back. */}
      {!bare && isStaff(me) && /^\/p\/[^/]+\/(?!client\/?$)./.test(pathname) ? <ProjectMenu projectId={pathname.split('/')[2]!} pathname={pathname} /> : null}
      {children}
      {foot ? <PortalFoot /> : null}
    </div>
  );
}

const VIEWS: [View, string][] = [
  ['admin', 'Admin'],
  ['client', 'Client'],
  ['sub', 'Sub'],
];

const ENVIRONMENTS: [Environment, string][] = [
  ['local', 'Local'],
  ['production', 'Production'],
];

/**
 * The strip above everything, for Jason: which data a local build is working
 * on (development only), and whose eyes he is seeing the portal through
 * (admins). Production is a bright bengara band that stays in view, and
 * seeing it as a client or a sub turns the strip slate, each with the words
 * that say why (guide §20.8: colour is never the only signal).
 */
function TopStrip({ env, me }: { env: Environment | null; me: MeDto | null }) {
  const { pathname } = useLocation();
  const production = env === 'production';
  const viewAs: ViewAs | null = me?.viewAs ?? null;
  const { data: health } = useQuery({ queryKey: ['health'], queryFn: () => api<HealthDto>('/health'), enabled: production, staleTime: 60_000 });
  const tone = production || viewAs ? 'dark' : 'light';
  const band = production ? 'bg-alert text-paper' : viewAs ? 'bg-slate text-paper' : 'border-b border-rule bg-sheet';
  return (
    <div className={`${band} ${production || viewAs ? 'sticky top-0 z-50' : ''}`} role="region" aria-label="Which data, and whose view">
      <div className="shell flex flex-wrap items-center gap-x-6 gap-y-2 py-2">
        {production ? (
          <p className="mr-auto text-[0.9375rem]">
            <strong className="font-medium">Production.</strong> Live data: changes are real, and clients see them.
            {health?.build.sha && health.build.sha !== 'dev' ? <span className="num ml-2 text-[0.75rem] opacity-80">build {health.build.sha.slice(0, 7)}</span> : null}
          </p>
        ) : viewAs ? (
          <p className="mr-auto text-[0.9375rem]">
            <strong className="font-medium">Seeing it as a {viewAs}.</strong>{' '}
            {env === 'local'
              ? 'Exactly what they see, and you can do what they can: it is done as you, on this machine’s data.'
              : 'Exactly what they see; changes are refused except on test projects, and a report you file goes in as yours.'}
          </p>
        ) : (
          <span className="mr-auto" />
        )}
        {env ? <Segmented label="Data" tone={tone} value={env} options={ENVIRONMENTS} onChange={(v) => switchEnvironment(v)} /> : null}
        {me?.canViewAs ? <Segmented label="View as" tone={tone} value={viewAs ?? 'admin'} options={VIEWS} onChange={(v) => void switchView(v, pathname)} /> : null}
      </div>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  tone,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
  tone: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return (
    <div className="flex items-center gap-2">
      <span className={`label ${dark ? '!text-paper' : ''}`}>{label}</span>
      <div role="group" aria-label={label} className={`flex overflow-hidden rounded-[2px] border ${dark ? 'border-paper/70' : 'border-rule-strong'}`}>
        {options.map(([v, words]) => (
          <button
            key={v}
            type="button"
            aria-pressed={value === v}
            onClick={() => value !== v && onChange(v)}
            className={`min-h-11 px-3 text-[0.8125rem] transition-colors duration-[120ms] sm:min-h-8 ${
              value === v ? (dark ? 'bg-paper text-ink' : 'bg-ink text-paper') : dark ? 'text-paper hover:bg-paper/15' : 'text-ink-2 hover:bg-inset hover:text-ink'
            }`}
          >
            {words}
          </button>
        ))}
      </div>
    </div>
  );
}
